import {
  useState,
  useRef,
  useEffect,
  useMemo,
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  KeyboardEvent,
} from 'react'
import { mergeRefs } from '@grupoboticario/flora-react-utils'
import { UseFloraInputProps } from './types'
import { getMaskedValue } from './lib/utils'

export const useFloraInput = ({
  forwardedRef,
  onChange,
  onKeyDown,
  onBlur,
  onMouseDownCapture,
  mask,
  defaultValue,
  value,
}: UseFloraInputProps) => {
  const internalRef = useRef<HTMLInputElement>(null)
  const [charCount, setCharCount] = useState(0)
  const [clicked, setClicked] = useState(false)
  const valueMasked = useMemo(() => {
    return (mask && getMaskedValue(String(value || ''), mask)) || value
  }, [mask, value])
  const defaultValueMasked = useMemo(() => {
    return (mask && getMaskedValue(String(defaultValue || ''), mask)) || defaultValue
  }, [mask, defaultValue])

  const handleOnMouseUpCaptureLabel = () => {
    setClicked(true)
  }

  const handleOnMouseDownCaptureInput = (e: MouseEvent<HTMLInputElement>) => {
    setClicked(true)

    if (onMouseDownCapture) {
      onMouseDownCapture(e)
    }
  }

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    setClicked(false)
    setCharCount(e.target.value.length)

    if (onBlur) {
      onBlur(e)
    }
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (mask && internalRef.current) {
      const newValue = getMaskedValue(internalRef.current?.value, mask) || ''
      internalRef.current.value = newValue
      e.target.value = newValue
    }

    if (onChange) {
      onChange(e)
    }
  }

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const inputElement = internalRef?.current
    const isKeyDelete = e.key?.toLowerCase() === 'backspace'
    const isObjectMask = typeof mask === 'object'
    const isPercentMask = isObjectMask && mask.style === 'percent'
    const isZero = Number(inputElement?.value.replace(/(\D)/g, '')) === 0

    if (isKeyDelete && isPercentMask && inputElement) {
      const value = inputElement.value || ''
      const newValue = value.replace('%', '')

      if (inputElement.selectionStart !== newValue.length) {
        inputElement.value = newValue.slice(0, newValue.length)
      }
    }

    if (isKeyDelete && isObjectMask && isZero && inputElement) {
      inputElement.value = ''
    }

    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  useEffect(() => {
    if (value) {
      setCharCount(String(value).length)
    }

    if (internalRef?.current) {
      setCharCount(internalRef.current.value.length)
    }
  }, [internalRef, value])

  return {
    charCount,
    clicked,
    valueMasked,
    defaultValueMasked,
    ref: mergeRefs(forwardedRef, internalRef),
    handleOnChange,
    handleOnMouseDownCaptureInput,
    handleOnMouseUpCaptureLabel,
    handleOnBlur,
    handleOnKeyDown,
  }
}
